import type { LucideIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '../lib/utils';

import { VButton } from './vButton';
import { VTooltip, VTooltipContent, VTooltipTrigger } from './vTooltip';

type VIconActionButtonProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'children' | 'aria-label'
> & {
  icon: LucideIcon;
  /** Toegankelijke naam van de knop. */
  label: string;
  /** Tooltip-inhoud; valt terug op `label`. `\n` blijft behouden. */
  tooltip?: React.ReactNode;
  /** Extra classes op het icoon, bv. `animate-spin`. */
  iconClassName?: string;
};

/**
 * Icon-only actieknop (ghost, sm) met tooltip die uitlegt wat de knop doet.
 * De tooltip-trigger zit op een wrappende span: VButton heeft `pointer-events-none`
 * wanneer disabled, waardoor een tooltip op de knop zelf dan nooit opent.
 */
export const VIconActionButton = React.forwardRef<HTMLButtonElement, VIconActionButtonProps>(
  function VIconActionButton(
    { icon: Icon, label, tooltip, iconClassName, disabled, ...props },
    ref,
  ) {
    return (
      <VTooltip>
        <VTooltipTrigger asChild>
          {/* Focusbaar enkel wanneer de knop disabled is, zodat de tooltip met het toetsenbord bereikbaar blijft. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <span className="inline-flex" tabIndex={disabled ? 0 : undefined}>
            <VButton
              ref={ref}
              variant="ghost"
              size="sm"
              aria-label={label}
              disabled={disabled}
              {...props}
            >
              <Icon className={cn('size-4 shrink-0', iconClassName)} aria-hidden />
            </VButton>
          </span>
        </VTooltipTrigger>
        <VTooltipContent className="whitespace-pre-line">{tooltip ?? label}</VTooltipContent>
      </VTooltip>
    );
  },
);
VIconActionButton.displayName = 'VIconActionButton';

export type { VIconActionButtonProps };
